import { BigNumber } from 'bignumber.js';
import { FieldError, FieldErrors, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import BookkeepingBalancingTooltip, {
  BalancingTooltipRow,
} from '~/components/bookkeeping/BookkepingBalancingTooltip/BookkeepingBalancingTooltip';
import Button from '~/components/buttons/NewButton';
import AlertMessage from '~/components/data-display/AlertMessage';
import { HStack, VStack } from '~/components/layout/Stack';
import IconSave from '~/icons/icon-save.svg?react';
import { useModal } from '~/modals/Modal';
import { useBookkeepingConfiguration } from '~/services/bookkeeping/bookkeeping-configuration';
import { useShowCompetencePeriodColumn, useShowDescriptionColumn } from '~/state/bookkeeping/columnVisibility';
import { RegistrationTemplate, RegistrationTemplateType } from '~/types/registration-template';
import { Transaction } from '~/types/transaction';
import { isDefined } from '~/types/typeGuards';
import { newMoney, subtractMoney, sumMoney } from '~/utils/money';
import CreateRegistrationTemplateModal from '~/views/bookkeeping/registration-templates/CreateRegistrationTemplateModal';
import styles from './BookkeepingTransactionRecap.module.scss';
import {
  BookkeepingEntryFormOutput,
  BookkeepingTransactionFormOutput,
  BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID,
} from './BookkeepingTransactionSchema';

interface Props {
  internalTransferTransaction?: Transaction | null;
  onClose: () => void;
  isUpdate: boolean;
  isSubmitting: boolean;
  autoFocusSubmit?: boolean;
  isManual?: boolean;
  disableSaveAsTemplate: boolean;
  onTemplateCreated?: (template: RegistrationTemplate) => void;
}

type ErrorNode =
  | FieldError
  | FieldErrors<BookkeepingTransactionFormOutput>
  | (FieldError | FieldErrors<BookkeepingTransactionFormOutput>)[]
  | undefined;

const BookkeepingTransactionRecap: React.FC<Props> = ({
  internalTransferTransaction,
  autoFocusSubmit,
  isUpdate,
  isSubmitting,
  onClose,
  isManual,
  disableSaveAsTemplate,
  onTemplateCreated,
}) => {
  const { t } = useTranslation('bookkeeping', { keyPrefix: 'transactions.create.form' });
  const { t: tTemplates } = useTranslation('bookkeeping', {
    keyPrefix: 'registrations.create.manual_registration.templates',
  });
  const { showModal } = useModal();
  const configuration = useBookkeepingConfiguration();
  const [showDescriptionColumn] = useShowDescriptionColumn();
  const [showCompetencePeriodColumn] = useShowCompetencePeriodColumn();

  const {
    control,
    getValues,
    formState: { errors, isDirty, isValid, submitCount },
  } = useFormContext<BookkeepingTransactionFormOutput>();

  const handleSaveAsTemplate = () => {
    const values = getValues();
    showModal({
      component: CreateRegistrationTemplateModal,
      props: {
        templateData: {
          templateType: RegistrationTemplateType.BOOKKEEPING_TRANSACTION,
          body: {
            description: values.description,
            view: {
              descriptionColumn: showDescriptionColumn,
              competencePeriodColumn: showCompetencePeriodColumn,
            },
            entries: values.entries.map((entry) => ({
              bookkeepingAccountId: entry.bookkeepingAccountId || null,
              counterpartId: entry.counterpart?.id || null,
              description: entry.description,
            })),
          },
        },
        onCreated: onTemplateCreated,
      },
    });
  };
  const entries = useWatch({ control, name: 'entries' });
  const totalDare = entries.reduce((acc, entry) => {
    if (entry.dare) {
      return sumMoney(acc, entry.dare);
    }
    return acc;
  }, newMoney(0));

  const totalAvere = entries.reduce((acc, entry) => {
    if (entry.avere) {
      return sumMoney(acc, entry.avere);
    }
    return acc;
  }, newMoney(0));

  const difference = subtractMoney(totalDare, totalAvere);
  const isBalanced = BigNumber(totalDare.amount).minus(totalAvere.amount).isZero();

  const accountIdsRequiringDocument = [
    configuration.accounts.debtsSuppliers,
    configuration.accounts.creditsClients,
  ].filter((id) => id !== null);

  const entriesWithDocumentWarning = entries.filter(
    (entry) => accountIdsRequiringDocument.includes(entry.bookkeepingAccountId) && !isDefined(entry.documentId),
  );

  const isFieldError = (value: FieldError | FieldErrors<BookkeepingTransactionFormOutput>): value is FieldError =>
    'message' in value || 'types' in value;

  const hasNonBalanceError = (value: ErrorNode): boolean => {
    if (!value) return false;
    if (Array.isArray(value)) {
      return value.some(hasNonBalanceError);
    }

    if (typeof value !== 'object') {
      return value !== BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID;
    }

    if (isFieldError(value)) {
      if (value.message && value.message !== BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID) {
        return true;
      }
      if (value.types) {
        const typeMessages = Object.values(value.types).flatMap((entry) => (Array.isArray(entry) ? entry : [entry]));
        if (typeMessages.some((entry) => entry !== BOOKKEEPINGTRANSACTIONNOTBALANCEDERRORID)) {
          return true;
        }
      }
    }

    return Object.values(value).some(hasNonBalanceError);
  };

  const hasRequiredFieldErrors = hasNonBalanceError(errors);
  const shouldShowValidationError = submitCount > 0 && !isValid && hasRequiredFieldErrors;
  const shouldShowUnbalancedError = submitCount > 0 && !hasRequiredFieldErrors && !isBalanced;
  const shouldShowInternalTransferInfo = isDefined(internalTransferTransaction) && !isDirty && !isUpdate;
  const missingAdjustmentAccount =
    Array.isArray(errors.entries) &&
    (errors.entries as (FieldErrors<BookkeepingEntryFormOutput> | undefined)[]).some((e) =>
      Boolean(e?.adjustmentAccountId),
    );

  return (
    <VStack gap={3} className={styles.recap}>
      {shouldShowValidationError && (
        <AlertMessage box fullWidth type="error">
          {missingAdjustmentAccount ? t('warn_missing_adjustment_account') : t('error_validation_form')}
        </AlertMessage>
      )}
      {shouldShowUnbalancedError && (
        <AlertMessage box fullWidth type="error">
          {t('error_unbalanced')}
        </AlertMessage>
      )}
      {entriesWithDocumentWarning.length > 0 && (
        <AlertMessage box fullWidth type="warning">
          {t('warn_missing_documents', { count: entriesWithDocumentWarning.length })}
        </AlertMessage>
      )}
      {shouldShowInternalTransferInfo && (
        <AlertMessage box fullWidth type="info">
          <HStack gap={0.5}>
            <strong>{t('info_internal_transfer_title')}</strong>
            <span>{t('info_internal_transfer_body')}</span>
          </HStack>
        </AlertMessage>
      )}
      <HStack justify="space-between" align="center">
        <BookkeepingBalancingTooltip
          isBalanced={isBalanced}
          labels={[t('balancing_tooltip.dare'), t('balancing_tooltip.avere'), t('balancing_tooltip.difference')]}
        >
          <BalancingTooltipRow
            isBalanced={isBalanced}
            label={t('balancing_tooltip.total')}
            values={[totalDare, totalAvere, difference]}
          />
        </BookkeepingBalancingTooltip>
        <HStack gap={2} justify="end" align="center" className={styles.actions}>
          {isManual && (
            <Button variant="link" Icon={IconSave} onClick={handleSaveAsTemplate} disabled={disableSaveAsTemplate}>
              {tTemplates('save_as_template')}
            </Button>
          )}
          <Button variant="outlined" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button loading={isSubmitting} type="submit" autoFocus={autoFocusSubmit}>
            {t(isUpdate ? 'update_transaction' : 'save_transaction')}
          </Button>
        </HStack>
      </HStack>
    </VStack>
  );
};

export default BookkeepingTransactionRecap;
